import requireElement from '../util/require_element'

/*
 * Export a chart as a PNG image client-side.
 * Usage:
 * - Either via the download_diagram_image_button() helper
 * - or with HTML that is structured as followed:
 *
 * [downloadable-chart-image][up-data={ rawFilename: STRING }]
 *   button[downloadable-chart-image--cta]
 *
 * The compiler scopes its chart lookup to the closest .diagram--content or
 * .stage-impact ancestor, so place the button as a sibling/descendant of
 * the chart's wrapper.
 */

up.compiler('[downloadable-chart-image]', (element, { rawFilename }) => {
  const button = requireElement(element, '[downloadable-chart-image--cta]')
  const MIME_TYPE = 'image/png'

  function init() {
    up.on(button, 'click', downloadImage)
  }

  function downloadImage() {
    const scope = element.closest('.diagram--content, .stage-impact')
    const chartElement = scope?.querySelector('[stacked-bar-chart]')
    if (!chartElement || !window.echarts) return

    const chart = window.echarts.getInstanceByDom(chartElement)
    if (!chart) return

    // Per-stage charts can be initialized while their .stage-impact wrapper is
    // display:none (canvas 0x0). Resizing here re-measures the visible
    // container before reading the canvas.
    chart.resize()

    // On the live chart, text uses --ui-primary (light), which becomes
    // unreadable on the PNG's white background. We also switch the legend
    // from the on-screen scroller to a plain layout so all entries wrap onto
    // multiple lines and remain visible in the exported image.
    const currentOption = chart.getOption()
    const originalColor = currentOption.legend?.[0]?.textStyle?.color
    const originalLegendType = currentOption.legend?.[0]?.type ?? 'plain'
    const originalGridTop = currentOption.grid?.[0]?.top ?? 90

    const exportColor = '#333'

    chart.setOption({
      legend: { type: 'plain', textStyle: { color: exportColor } },
      xAxis: [{ axisLabel: { color: exportColor } }],
      yAxis: [{ axisLabel: { color: exportColor } }],
      grid: { top: Math.max(originalGridTop, 130) },
    })

    const dataURL = chart.getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: '#fff',
    })

    chart.setOption({
      legend: { type: originalLegendType, textStyle: { color: originalColor } },
      xAxis: [{ axisLabel: { color: originalColor } }],
      yAxis: [{ axisLabel: { color: originalColor } }],
      grid: { top: originalGridTop },
    })

    sendFile(sanitizeFilename(rawFilename), dataURL)
  }

  function sanitizeFilename(rawFileName) {
    return (rawFileName.toLowerCase().trim().replace(/[^A-z0-9]/g, '_') + '.png').replace(/__+/g, '_')
  }

  function sendFile(fileName, dataURL) {
    if (element.mockImageDownload) {
      window.lastImageDownload = {
        fileName,
        dataURL,
        mimeType: MIME_TYPE,
      }
      return
    }
    const temporaryLink = up.element.affix(element, 'a.visually-hidden', {
      download: fileName,
      href: dataURL,
    })
    temporaryLink.click()
    temporaryLink.remove()
  }

  init()
})
